import { h, empty } from '../../../../src/shared/components/dom.js';
import { Spinner } from '../../../../src/shared/components/spinner.js';
import { getLists, createList, addListItem, removeListItem } from '../api.js';
import { isQueued } from '../queue-state.js';

/**
 * Modal for adding a game to one or more lists.
 *
 * Invokable from anywhere that has a {game_id, title}:
 *   openAddToListModal({ gameId: 42, title: 'Super Mario Bros.' });
 *
 * UX:
 *   1. Fetch the user's lists on open.
 *   2. Show each list as a checkbox row: ✓ if the game is already in
 *      it, empty otherwise. Clicking toggles membership via
 *      addListItem / removeListItem (optimistic, with rollback on
 *      failure — though failures are rare since the server is
 *      idempotent).
 *   3. A "+ New list" inline affordance lets the user create a list
 *      and add the game in one go.
 *
 * Implementation notes:
 *   - Single globally-mounted modal element. Repeated opens reuse it.
 *   - 401 → sign-in nudge instead of the checkbox list.
 *   - We don't try to maintain cross-tab state — closing the modal
 *     and re-opening pulls fresh data from the server.
 */

let modalEl = null;
let escListener = null;
let flashEl = null;
let flashTimer = null;

export async function openAddToListModal({ gameId, title }) {
  if (!Number.isFinite(gameId) || gameId <= 0) {
    console.warn('[add-to-list] missing game_id');
    return;
  }
  ensureModal();
  const gameTitle = title || 'Game';
  const title$ = modalEl.querySelector('.lib-atl-title');
  const body = modalEl.querySelector('.lib-atl-body');
  title$.textContent = `Add "${title || 'game'}" to…`;
  empty(body);
  body.appendChild(Spinner({ label: 'Loading your lists…' }));
  show();

  let lists;
  try {
    // Ask the owner-list endpoint to include database-backed membership
    // for this game so every row is correct on first render.
    lists = await getLists({ game_id: gameId });
    // The queue is intentionally included here (it is pinned first by
    // GET /lists) so the add-to-list button can target it too; its row
    // is badged + pre-checked in renderRow.
  } catch (err) {
    empty(body);
    if (err?.status === 401) {
      body.appendChild(h('p', { class: 'lib-atl-hint' },
        'Sign in from the main app to create and manage lists.'));
    } else {
      body.appendChild(h('p', { class: 'lib-atl-hint' }, 'Could not load your lists.'));
    }
    return;
  }

  empty(body);

  // Rows: one per list. Each row is a button that toggles membership.
  // We treat "in the list" as a per-row `data-in` flag and re-render
  // the check icon when it flips.
  if (lists.length === 0) {
    body.appendChild(h('p', { class: 'lib-atl-hint' },
      'You don\'t have any lists yet. Create one below.'));
  } else {
    const rows = h('div', { class: 'lib-atl-rows' });
    for (const list of lists) {
      rows.appendChild(renderRow(list, gameId, gameTitle));
    }
    body.appendChild(rows);
  }

  // "+ New list" row with inline text input. Keyboard-friendly:
  // Enter submits, Escape closes the whole modal.
  body.appendChild(renderCreateRow(gameId, gameTitle));
}

// ── Modal lifecycle ──────────────────────────────────────────────────

function ensureModal() {
  if (modalEl) return;
  modalEl = h('div', {
    class: 'lib-atl-modal hidden',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': 'lib-atl-title',
  },
    h('div', { class: 'lib-atl-content' },
      h('div', { class: 'lib-atl-header' },
        h('span', { id: 'lib-atl-title', class: 'lib-atl-title' }, 'Add to list'),
        h('button', {
          class: 'lib-atl-close',
          type: 'button',
          'aria-label': 'Close',
          onclick: hide,
        }, '✕'),
      ),
      h('div', { class: 'lib-atl-body' }),
    ),
  );
  // Click outside closes. stopPropagation on the content so clicks
  // inside the card don't bubble to the backdrop listener.
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) hide();
  });
  modalEl.querySelector('.lib-atl-content').addEventListener('click', (e) => e.stopPropagation());
  document.body.appendChild(modalEl);
}

function show() {
  modalEl.classList.remove('hidden');
  // Escape-to-close — bound on open, unbound on close so the
  // listener doesn't leak.
  escListener = (e) => { if (e.key === 'Escape') hide(); };
  document.addEventListener('keydown', escListener);
}

function hide() {
  if (!modalEl) return;
  modalEl.classList.add('hidden');
  if (escListener) {
    document.removeEventListener('keydown', escListener);
    escListener = null;
  }
}

function showFlash(message) {
  if (!flashEl || !flashEl.isConnected) {
    flashEl = h('div', {
      class: 'lib-atl-flash',
      role: 'status',
      'aria-live': 'polite',
      'aria-atomic': 'true',
      hidden: true,
    });
    document.body.appendChild(flashEl);
  }
  // Make the live region visible before changing its text so assistive
  // technology observes the mutation.
  flashEl.hidden = false;
  flashEl.textContent = message;
  flashEl.classList.add('show');
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    flashEl.classList.remove('show');
    flashEl.hidden = true;
  }, 7000);
}

// ── Row renderers ────────────────────────────────────────────────────

/** One row per existing list. Clicking toggles game membership. */
function renderRow(list, gameId, gameTitle) {
  const isQueue = list.kind === 'queue';
  const checkbox = h('span', { class: 'lib-atl-check' }, '');
  const name = h('span', { class: 'lib-atl-row-name', title: list.name }, list.name);
  const badge = isQueue
    ? h('span', { class: 'lib-atl-queue-badge', title: 'Your built-in play queue' }, 'QUEUE')
    : null;
  const row = h('button', {
    class: `lib-atl-row${isQueue ? ' is-queue' : ''}`,
    type: 'button',
    'data-in': 'unknown',    // 'in' | 'out' | 'unknown' (before first click)
    'data-busy': 'false',
    onclick: async () => {
      if (row.dataset.busy === 'true') return;
      row.dataset.busy = 'true';
      try {
        if (row.dataset.in === 'in') {
          await removeListItem(list.id, gameId);
          setRowState(row, 'out');
          adjustRowCount(row, -1);
          showFlash(`${gameTitle} removed from list ${list.name}`);
        } else {
          const res = await addListItem(list.id, gameId);
          setRowState(row, 'in');
          if (res?.added === false) {
            showFlash(`${gameTitle} is already in list ${list.name}`);
          } else {
            adjustRowCount(row, 1);
            showFlash(`${gameTitle} added to list ${list.name}`);
          }
        }
      } catch (err) {
        showFlash(`Could not update list ${list.name}`);
        console.error('[add-to-list] toggle failed:', err);
      } finally {
        row.dataset.busy = 'false';
      }
    },
  },
    checkbox,
    name,
    badge,
    h('span', { class: 'lib-atl-row-count' }, `${list.item_count || 0}`),
  );
  // New servers return authoritative membership for every row. Retain the
  // queue-state fallback for a cached client talking to an older server.
  if (typeof list.contains_game === 'boolean') {
    setRowState(row, list.contains_game ? 'in' : 'out');
  } else if (isQueue) {
    setRowState(row, isQueued(gameId) ? 'in' : 'out');
  }
  return row;
}

function setRowState(row, state) {
  row.dataset.in = state;
  const check = row.querySelector('.lib-atl-check');
  check.textContent = state === 'in' ? '✓' : '';
  row.classList.toggle('in-list', state === 'in');
  if (state === 'unknown') row.removeAttribute('aria-pressed');
  else row.setAttribute('aria-pressed', state === 'in' ? 'true' : 'false');
}

function adjustRowCount(row, delta) {
  const count = row.querySelector('.lib-atl-row-count');
  const current = Number(count.textContent) || 0;
  count.textContent = String(Math.max(0, current + delta));
}

/** The "+ New list" row with inline create input. */
function renderCreateRow(gameId, gameTitle) {
  const input = h('input', {
    type: 'text',
    class: 'lib-atl-create-input',
    placeholder: 'New list name…',
    'aria-label': 'New list name',
    maxlength: '80',
  });
  const msg = h('span', { class: 'lib-atl-create-status', role: 'status' }, '');

  async function submit() {
    const name = input.value.trim();
    if (!name) return;
    input.disabled = true;
    msg.textContent = '';
    try {
      const created = await createList({ name });
      // Immediately add the game to the new list so the click is a
      // one-shot, not two steps.
      await addListItem(created.id, gameId);
      input.value = '';
      // Also add a fresh checked row so the user sees the result
      // without closing + reopening the modal.
      const rows = modalEl.querySelector('.lib-atl-rows') || (() => {
        const r = h('div', { class: 'lib-atl-rows' });
        msg.parentElement.parentElement.insertBefore(r, msg.parentElement);
        return r;
      })();
      const freshRow = renderRow({ ...created, item_count: 1 }, gameId, gameTitle);
      setRowState(freshRow, 'in');
      rows.appendChild(freshRow);
      showFlash(`${gameTitle} added to list ${created.name || name}`);
    } catch (err) {
      msg.textContent = err?.body?.error === 'name_taken'
        ? 'You already have a list with that name'
        : 'Could not create';
      console.error('[add-to-list] create failed:', err);
    } finally {
      input.disabled = false;
    }
  }

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
  });

  return h('div', { class: 'lib-atl-create-row' },
    input,
    h('button', {
      class: 'lib-btn',
      type: 'button',
      onclick: submit,
    }, 'Create'),
    msg,
  );
}
